import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, formatApiError } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'Set New Password | Toronto Royale Admin';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Missing reset token. Please use the link from your email.');
      return;
    }
    if (newPw.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPw !== confirmPw) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/reset-password`,
        { token, new_password: newPw },
        { withCredentials: true }
      );
      // Cookies are set by the backend → just refresh the auth context and go to /admin
      await refresh();
      navigate('/admin', { replace: true });
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(formatApiError(err.response.data.detail));
      } else if (err.request) {
        setError('Cannot reach the server. Please try again.');
      } else {
        setError(err.message || 'Reset failed.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-reset-password-page" style={{ minHeight: '100vh', backgroundColor: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 30%, rgba(194,168,120,0.06) 0%, transparent 60%)', pointerEvents: 'none' }} />

      <form onSubmit={handleSubmit} data-testid="admin-reset-password-form" style={{ width: '100%', maxWidth: '420px', position: 'relative', zIndex: 10, backgroundColor: 'rgba(14,14,14,0.6)', border: '1px solid rgba(194,168,120,0.1)', padding: '56px 44px', backdropFilter: 'blur(12px)', boxShadow: '0 24px 80px rgba(0,0,0,0.5)' }}>
        <div style={{ position: 'absolute', top: 0, left: '15%', right: '15%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.35), transparent)' }} />
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <p style={{ fontSize: '10px', color: 'rgba(194,168,120,0.6)', letterSpacing: '5px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '14px' }}>Toronto Royale</p>
          <h1 className="font-serif" style={{ fontSize: '24px', fontWeight: 500, color: '#FAFAFA', letterSpacing: '0.5px' }}>Set New Password</h1>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '11px', color: 'rgba(194,168,120,0.7)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '10px', fontWeight: 400 }}>New Password (min 8 chars)</label>
          <input
            data-testid="admin-reset-new-password"
            type="password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            required
            autoComplete="new-password"
            style={{ width: '100%', backgroundColor: 'rgba(8,8,8,0.7)', border: '1px solid rgba(194,168,120,0.15)', color: '#F5F5F5', padding: '13px 16px', fontSize: '14px', outline: 'none' }}
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '11px', color: 'rgba(194,168,120,0.7)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '10px', fontWeight: 400 }}>Confirm New Password</label>
          <input
            data-testid="admin-reset-confirm-password"
            type="password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            required
            autoComplete="new-password"
            style={{ width: '100%', backgroundColor: 'rgba(8,8,8,0.7)', border: '1px solid rgba(194,168,120,0.15)', color: '#F5F5F5', padding: '13px 16px', fontSize: '14px', outline: 'none' }}
          />
        </div>

        {error && (
          <div data-testid="admin-reset-password-error" style={{ marginBottom: '18px', padding: '11px 13px', backgroundColor: 'rgba(180,40,40,0.08)', border: '1px solid rgba(220,80,80,0.25)', color: '#E89B9B', fontSize: '12px' }}>
            {error}
          </div>
        )}

        <button
          data-testid="admin-reset-password-submit"
          type="submit"
          disabled={submitting}
          style={{ width: '100%', padding: '14px', backgroundColor: '#C2A878', color: '#0A0A0A', fontSize: '12px', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', border: 'none', cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.6 : 1 }}
        >
          {submitting ? 'Updating…' : 'Set New Password'}
        </button>

        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <Link to="/admin/login" data-testid="admin-back-to-login" style={{ fontSize: '12px', color: 'rgba(194,168,120,0.7)', textDecoration: 'none', letterSpacing: '0.5px' }}>
            &larr; Back to login
          </Link>
        </div>
      </form>
    </div>
  );
};
